import { Icon } from '@/components/ui/Icon/Icon';
import { nb } from '@/i18n/nb';
import type { PoiCategory } from '@/types/api';
import { POI_CATEGORIES } from './poiCategories';
import s from './FilterChips.module.css';

type FilterChipsProps = {
  active: Set<PoiCategory>;
  onToggle: (id: PoiCategory) => void;
};

/*
 * Top-left row of seven pills, one per category. Each chip is a `role="switch"`
 * (boolean on/off) — preferred over a generic button because the chip directly
 * mirrors "category visible?", and SR users hear "switch on / off".
 *
 * Each chip is its own Tab stop. The reason GATE-1 still saw a long wrap back
 * to the first chip wasn't the chip-row structure; it was that focus wasn't
 * trapped inside the map view (so Tab walked through the underlying landing
 * page chrome too). The fix lives on the parent `<Utforsk>`: a `useFocusTrap`
 * on the map-view root keeps the tab cycle tight (chips → close → MapLibre
 * nav → mode toggle → back to first chip).
 */
export function FilterChips({ active, onToggle }: FilterChipsProps) {
  return (
    <div className={s.chips} role="group" aria-label={nb.map.title}>
      {POI_CATEGORIES.map((cat) => {
        const isOn = active.has(cat.id);
        return (
          <button
            key={cat.id}
            type="button"
            role="switch"
            aria-checked={isOn}
            className={`${s.chip} ${isOn ? s.on : s.off}`}
            // Hand the category colour in via a CSS variable so the same rule
            // can drive the dot + the on-state fill without per-id CSS classes.
            style={{ ['--chip-color' as string]: `var(${cat.tokenVar})` }}
            onClick={() => onToggle(cat.id)}
          >
            <Icon name={cat.icon} size={14} />
            <span>{nb.map.categories[cat.labelKey]}</span>
          </button>
        );
      })}
    </div>
  );
}
